<script setup lang="ts">
// Desktop sidebar — the single navigation chrome for every desktop route
// (Home · Live · Events · Guide · Library · Favourites · Recordings ·
// Manage + Account). Shown at >= --bp-lg on all non-public routes (App.vue
// gates via `showRail`); below that the BottomTabBar owns navigation.
// Replaces the former top NavBar, which was deleted when this rail went
// universal.
//
// EXPANDABLE: collapsed = 64px icon rail (labels sr-only + title tooltips);
// a toggle expands it to a ~220px labeled sidebar. State persists in
// useFiltersStore.navExpanded. The width flip is INSTANT — no CSS transition on
// the aside — because on /live the fixed global player's frame tracks the
// reflow via its own 280ms transition; a second ease here would make it drift.
// The Account menu carries only the overflow destinations (admin-gated
// Sources/Settings/Logs, Install, Sign out). Grew out of atlas-mockup.jsx's
// AppRail (frames 01–14); Events sits directly after Live (the product spine).

import { computed, onBeforeUnmount, ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useFiltersStore } from '@/stores/filters';
import { useInstallPrompt } from '@/composables/useInstallPrompt';
import AtlasMark from '@/components/brand/AtlasMark.vue';
import Wordmark from '@/components/brand/Wordmark.vue';
import { isLibraryActive } from './_navMatch';
import { useLayoutTier } from '@/composables/useLayoutTier';

const route = useRoute();
const auth = useAuthStore();
const filters = useFiltersStore();
const installPrompt = useInstallPrompt();

const { tier } = useLayoutTier();
/**
 * The rail is LOCKED COLLAPSED below 1024 (mobile-foldable-redesign §2.2). The
 * persisted preference is ignored there, not overwritten — a user who expands
 * the rail on a desktop and then opens the app on a 752px foldable would
 * otherwise get a 220px rail on a screen with 688px of content, and unfolding
 * back would have lost their desktop choice.
 */
const isDesktopTier = computed(() => tier.value === 'desktop');
const accountOpen = ref(false);
const expanded = computed(() => isDesktopTier.value && filters.data.navExpanded);

function toggleExpanded() {
    filters.patch({ navExpanded: !filters.data.navExpanded });
}

const homeActive = computed(() => route.path === '/home' || route.path === '/');
const liveActive = computed(() => route.path === '/live');
const eventsActive = computed(() => route.path === '/events');
const guideActive = computed(() => route.path === '/guide');
const libraryActive = computed(() => isLibraryActive(route.path));
const favouritesActive = computed(() => route.path === '/favourites');
const recordingsActive = computed(() => route.path === '/recordings' || route.path.startsWith('/recordings/'));
const manageActive = computed(() => route.path === '/manage' || route.path.startsWith('/manage/'));

function closeAccount() {
    accountOpen.value = false;
    document.removeEventListener('click', onDocClick, true);
    document.removeEventListener('keydown', onKeydown, true);
}

function onDocClick(e: MouseEvent) {
    const root = railRef.value;
    if (root && !root.contains(e.target as Node)) closeAccount();
}

function onKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') closeAccount();
}

const railRef = ref<HTMLElement | null>(null);

function toggleAccount() {
    accountOpen.value = !accountOpen.value;
    if (accountOpen.value) {
        // Capture-phase so a click anywhere outside the rail closes it before the
        // target handles it.
        document.addEventListener('click', onDocClick, true);
        document.addEventListener('keydown', onKeydown, true);
    } else {
        closeAccount();
    }
}

async function logout() {
    closeAccount();
    await auth.logout().catch(() => {});
    location.href = '/login';
}

async function install() {
    await installPrompt.installApp();
    closeAccount();
}

onBeforeUnmount(closeAccount);
</script>

<template>
    <aside ref="railRef" class="app-rail" :class="{ expanded }" aria-label="Primary">
        <div class="rail-header">
            <RouterLink to="/home" class="rail-brand" aria-label="Atlas — Home">
                <Wordmark v-if="expanded" :size="18" />
                <AtlasMark v-else :size="26" />
            </RouterLink>
            <button
                v-if="isDesktopTier"
                type="button"
                class="rail-toggle"
                :aria-expanded="expanded"
                aria-controls="rail-nav"
                :aria-label="expanded ? 'Collapse navigation' : 'Expand navigation'"
                :title="expanded ? 'Collapse navigation' : 'Expand navigation'"
                @click="toggleExpanded"
            >
                <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M9 6l6 6-6 6" />
                </svg>
            </button>
        </div>

        <nav id="rail-nav" class="rail-nav" aria-label="Primary navigation">
            <RouterLink to="/home" class="rail-item" :class="{ active: homeActive }" :aria-current="homeActive ? 'page' : undefined" :title="expanded ? undefined : 'Home'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M3 11l9-8 9 8M5 10v10h14V10" />
                </svg>
                <span>Home</span>
            </RouterLink>
            <RouterLink to="/live" class="rail-item" :class="{ active: liveActive }" :aria-current="liveActive ? 'page' : undefined" :title="expanded ? undefined : 'Live'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="2" y="6" width="20" height="13" rx="2" />
                    <path d="M8 3l4 3 4-3" />
                </svg>
                <span>Live</span>
            </RouterLink>
            <RouterLink to="/events" class="rail-item" :class="{ active: eventsActive }" :aria-current="eventsActive ? 'page' : undefined" :title="expanded ? undefined : 'Events'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M13 2L4 14h6l-1 8 9-12h-6z" />
                </svg>
                <span>Events</span>
            </RouterLink>
            <RouterLink to="/guide" class="rail-item" :class="{ active: guideActive }" :aria-current="guideActive ? 'page' : undefined" :title="expanded ? undefined : 'Guide'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M3 9h18M8 2v4M16 2v4" />
                </svg>
                <span>Guide</span>
            </RouterLink>
            <RouterLink to="/movies" class="rail-item" :class="{ active: libraryActive }" :aria-current="libraryActive ? 'page' : undefined" :title="expanded ? undefined : 'Library'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="3" y="3" width="7" height="7" rx="1" />
                    <rect x="14" y="3" width="7" height="7" rx="1" />
                    <rect x="3" y="14" width="7" height="7" rx="1" />
                    <rect x="14" y="14" width="7" height="7" rx="1" />
                </svg>
                <span>Library</span>
            </RouterLink>
            <RouterLink to="/favourites" class="rail-item" :class="{ active: favouritesActive }" :aria-current="favouritesActive ? 'page' : undefined" :title="expanded ? undefined : 'Favourites'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M12 3.5l2.76 5.59 6.17.9-4.47 4.36 1.06 6.15L12 17.5l-5.52 2.9 1.06-6.15-4.47-4.36 6.17-.9L12 3.5z" />
                </svg>
                <span>Favourites</span>
            </RouterLink>
            <RouterLink to="/recordings" class="rail-item" :class="{ active: recordingsActive }" :aria-current="recordingsActive ? 'page' : undefined" :title="expanded ? undefined : 'Recordings'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="12" r="9" />
                    <circle cx="12" cy="12" r="3.5" fill="currentColor" stroke="none" />
                </svg>
                <span>Recordings</span>
            </RouterLink>
            <RouterLink to="/manage" class="rail-item" :class="{ active: manageActive }" :aria-current="manageActive ? 'page' : undefined" :title="expanded ? undefined : 'Manage'">
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M4 6h16M4 12h16M4 18h16" />
                    <circle cx="9" cy="6" r="2" fill="var(--color-bg-secondary)" />
                    <circle cx="15" cy="12" r="2" fill="var(--color-bg-secondary)" />
                    <circle cx="9" cy="18" r="2" fill="var(--color-bg-secondary)" />
                </svg>
                <span>Manage</span>
            </RouterLink>
        </nav>

        <div class="rail-spacer"></div>

        <div class="rail-account">
            <button
                type="button"
                class="rail-item"
                :class="{ active: accountOpen }"
                :aria-expanded="accountOpen"
                aria-haspopup="menu"
                :title="expanded ? undefined : 'Account'"
                @click="toggleAccount"
            >
                <svg class="ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 21c0-4 4-6 8-6s8 2 8 6" />
                </svg>
                <span>Account</span>
            </button>
            <div v-if="accountOpen" class="account-menu" role="menu" aria-label="Account">
                <!-- U7 (ux-remediation X5) — identity line so a signed-in
                     user can see who they are + their role at a glance. -->
                <p v-if="auth.user" class="account-identity" data-testid="account-identity">
                    Signed in as {{ auth.user.username }} · {{ auth.user.role }}
                </p>
                <!-- D8 final (ux-remediation X5) — Settings is reachable by
                     every role now (viewers get their own editable player
                     preferences there); Sources + Logs stay admin-only. -->
                <RouterLink to="/settings" class="account-link" role="menuitem" @click="closeAccount">Settings</RouterLink>
                <template v-if="auth.isAdmin">
                    <RouterLink to="/sources" class="account-link" role="menuitem" @click="closeAccount">Sources</RouterLink>
                    <RouterLink to="/dev/logs" class="account-link" role="menuitem" @click="closeAccount">Logs</RouterLink>
                </template>
                <hr class="account-sep" />
                <!-- Outside the isAdmin block on purpose: About carries the
                     required TMDB attribution, which every viewer must reach. -->
                <RouterLink to="/about" class="account-link" role="menuitem" @click="closeAccount">About</RouterLink>
                <button v-if="installPrompt.canInstall.value" type="button" class="account-link" role="menuitem" @click="install">Install app</button>
                <button v-if="auth.isAuthed" type="button" class="account-link account-signout" role="menuitem" @click="logout">Sign out</button>
            </div>
        </div>
    </aside>
</template>

<style scoped>
.app-rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 0 0 var(--rail-width);
    width: var(--rail-width);
    height: 100%;
    padding: var(--space-md) 0;
    gap: var(--space-xs);
    background: var(--color-bg-secondary);
    border-right: 1px solid var(--color-border);
    /* NO width transition — the flip is instant so the /live player frame (which
       tracks this reflow) animates once via its own transition, not twice. */
}

.app-rail.expanded {
    flex-basis: var(--rail-width-expanded);
    width: var(--rail-width-expanded);
    align-items: stretch;
    padding: var(--space-md) var(--space-sm);
}

/* ---- header: brand + expand/collapse toggle ---- */
.rail-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--space-xs);
    width: 100%;
    margin-bottom: var(--space-sm);
}

.app-rail.expanded .rail-header {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0 var(--space-xs);
}

.rail-brand {
    display: inline-flex;
    text-decoration: none;
}

.rail-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: 1px solid transparent;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--color-text-muted);
    cursor: pointer;
    transition:
        color var(--transition-fast),
        background var(--transition-fast);
}

.rail-toggle:hover,
.rail-toggle:focus-visible {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
    outline: none;
}

.rail-toggle .chev {
    width: 18px;
    height: 18px;
    transition: transform var(--transition-fast);
}

.app-rail.expanded .rail-toggle .chev {
    transform: rotate(180deg);
}

/* ---- nav ---- */
.rail-nav {
    display: flex;
    flex-direction: column;
    gap: var(--space-xs);
    width: 100%;
    align-items: center;
    /* The rail is `height: 100%` of the shell, so on a short viewport its items
       overflow. Scroll rather than clip — see the (max-height: 500px) block at
       the bottom of this file. `min-height: 0` is what lets a flex child shrink
       below its content height and actually become scrollable. */
    min-height: 0;
    overflow-y: auto;
    scrollbar-width: none;
}

.rail-nav::-webkit-scrollbar {
    display: none;
}

.app-rail.expanded .rail-nav {
    align-items: stretch;
}

.rail-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    width: 48px;
    min-height: 48px; /* touch target — spec §2.4 */
    padding: var(--space-xs) 0;
    border: 1px solid transparent;
    border-radius: var(--radius-md);
    background: transparent;
    color: var(--color-text-muted);
    text-decoration: none;
    font-size: 9px;
    font-weight: 600;
    cursor: pointer;
    transition:
        color var(--transition-fast),
        background var(--transition-fast),
        border-color var(--transition-fast);
}

.app-rail.expanded .rail-item {
    flex-direction: row;
    justify-content: flex-start;
    gap: var(--space-sm);
    width: 100%;
    min-height: 44px;
    padding: var(--space-sm) var(--space-md);
    font-size: var(--fs-md);
}

.rail-item .ico {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
}

/* Collapsed: labels are sr-only (accessible name preserved; icon-only visually).
   NOT display:none — that would drop the item's name from the a11y tree. */
.rail-item span {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
}

.app-rail.expanded .rail-item span {
    position: static;
    width: auto;
    height: auto;
    margin: 0;
    overflow: visible;
    clip: auto;
    white-space: normal;
}

.rail-item:hover,
.rail-item:focus-visible {
    color: var(--color-text-primary);
    outline: none;
}

.rail-item.active {
    color: var(--color-accent);
    background: var(--color-accent-dim);
    border-color: var(--color-accent-deep);
}

.rail-spacer {
    flex: 1;
}

.rail-account {
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
}

.app-rail.expanded .rail-account {
    justify-content: stretch;
}

.account-menu {
    position: absolute;
    bottom: 0;
    left: calc(100% + var(--space-xs));
    z-index: var(--z-dropdown);
    min-width: 184px;
    padding: var(--space-xs);
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-lg);
}

.account-identity {
    margin: 0 0 var(--space-xs);
    padding: var(--space-xs) var(--space-md);
    font-size: var(--fs-xs);
    color: var(--color-text-muted);
    white-space: nowrap;
}

.account-link {
    display: block;
    width: 100%;
    text-align: left;
    padding: var(--space-sm) var(--space-md);
    border-radius: var(--radius-sm);
    color: var(--color-text-secondary);
    text-decoration: none;
    font-size: var(--fs-sm);
    background: transparent;
    transition:
        color var(--transition-fast),
        background var(--transition-fast);
}

.account-link:hover,
.account-link:focus-visible {
    color: var(--color-text-primary);
    background: var(--color-bg-hover);
    outline: none;
}

.account-signout:hover,
.account-signout:focus-visible {
    color: var(--color-error);
}

.account-sep {
    border: 0;
    border-top: 1px solid var(--color-border);
    margin: var(--space-xs) 0;
}

/* Phone-only exception — the BottomTabBar owns navigation there. From 640 up
   the rail is the navigation on every tier (mobile-foldable-redesign §2.2). */
@media (max-width: 639px) { /* --bp-sm */
    .app-rail {
        display: none;
    }
}

/* Touch tiers — the rail IS the navigation from 640 up, so every one of its
   interactive elements needs a thumb-sized hit rect. The 48px `.rail-item`s
   already have one; the brand mark is a RouterLink to /home sized to its 26px
   glyph, which is a 26×26 tap target for a real destination. Widened here only
   (desktop keeps the compact header it has always had — a mouse does not need
   the extra 18px, and growing it there would push every rail item down for no
   reason). */
@media (max-width: 1023px) { /* --bp-xl */
    .rail-brand {
        align-items: center;
        justify-content: center;
        min-width: 44px;
        min-height: 44px;
    }

    /* Same reason, and these are the ones a thumb reaches LAST: the Account
       popover is where Settings, Sources, Logs, About, Install and Sign out
       live, and at `--fs-sm` over `--space-sm` padding each row was 31px. */
    .account-link {
        display: flex;
        align-items: center;
        min-height: 44px;
    }
}

/* Short viewports (the cover screen rotated is 840×360): nine 48px items plus
   the Account button do not fit in 360px of height, and a rail that CLIPS its
   last items is worse than one that scrolls. The brand mark goes first — it is
   a destination the Home rail item already offers, so it is the only one that
   costs nothing to drop. */
@media (max-height: 500px) { /* feature: short-viewport */
    .rail-brand {
        display: none;
    }
}

@media (prefers-reduced-motion: reduce) /* feature: reduced-motion */ {
    .rail-toggle .chev {
        transition: none;
    }
}
</style>
